/**
 * D'où viennent les présences que les écrans montrent.
 *
 * Les écrans (`components/presences/EcransDesPresences.tsx`) ne connaissent
 * qu'une `SourceDesPresences`. Deux existent :
 *
 * - **le lien** d'un centre et d'une semaine, avec son code, pour le téléphone
 *   de l'animateur (ici, sur `lien.supabase.ts`) ;
 * - **la session** de la coordination, pour un centre et une semaine qu'elle
 *   choisit (`source.session.ts`), avec les droits de son rôle.
 *
 * `codeRequis` n'a de sens que pour le lien ; la session ne le rend jamais.
 */
import type { JourCle, LectureDeFiche, LectureDuLien } from './lien';
import type { LectureDesEssentiels } from './essentiels';
import {
  lireLaFiche,
  lireLeLien,
  lireLesEssentiels,
  lireLesPhotos,
  noter,
  pointer,
  pointerLesAutres,
  type LectureDesPhotos,
  type ResultatDuGeste,
  type ResultatDuGroupe,
} from './lien.supabase';

export { adresseDeLaVue, vueDepuisLAdresse, type Vue } from './vue';

export interface SourceDesPresences {
  lireListe(): Promise<LectureDuLien>;
  pointer(registrationId: string, jour: JourCle, present: boolean | null): Promise<ResultatDuGeste>;
  noter(registrationId: string, notes: string): Promise<ResultatDuGeste>;
  pointerLesAutres(registrationIds: readonly string[], jour: JourCle, present: boolean): Promise<ResultatDuGroupe>;
  lireEssentiels(): Promise<LectureDesEssentiels>;
  lirePhotos(): Promise<LectureDesPhotos>;
  lireFiche(registrationId: string): Promise<LectureDeFiche>;
}

/** Le lien de l'animateur : tout passe par le jeton et le code, la base juge. */
export function sourceDuLien(jeton: string, code: string | null): SourceDesPresences {
  return {
    lireListe: () => lireLeLien(jeton, code),
    pointer: (registrationId, jour, present) => pointer(jeton, code, registrationId, jour, present),
    noter: (registrationId, notes) => noter(jeton, code, registrationId, notes),
    pointerLesAutres: (ids, jour, present) => pointerLesAutres(jeton, code, ids, jour, present),
    lireEssentiels: () => lireLesEssentiels(jeton, code),
    lirePhotos: () => lireLesPhotos(jeton, code),
    lireFiche: (registrationId) => lireLaFiche(jeton, code, registrationId),
  };
}
